import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	OnDestroy,
	Output,
} from '@angular/core';
import {
	FormBuilder,
	FormGroup,
	FormControl,
	Validators,
} from '@angular/forms';
import { Subscription as RxJsSubscription } from 'rxjs';
import {
	ErrorService,
	ExplainErrorsService,
	SessionService,
} from '@app/services';
import { environment } from '@env/environment';
import { User } from '../user';
import { UserService } from '../user.service';
@Component({
	selector: 'hpf-user-form',
	templateUrl: './user-form.component.html',
})
export class UserFormComponent implements OnInit, OnDestroy {
	/** The model subscription */
	private modelSubscription: RxJsSubscription;
	/** The user to inject in the form */
	@Input() user: User;
	/** Called the save button is clicked for a new instance */
	@Output() create = new EventEmitter<User>();
	/** Called the save button is clicked for an existing instance */
	@Output() update = new EventEmitter<void>();
	/** Called the delete button is clicked */
	@Output() delete = new EventEmitter<void>();
	/** Set loading state of the form */
	@Input() loading = false;
	/** Enable the deletion button */
	@Input() deletable = true;
	/** Enabled quick form */
	@Input() quickFormEnabled = environment.plugins.quickForm.enabled;
	/** Denotes if deltion in progress */
	deleting = false;
	/** Show delete modal */
	deleteModal = false;
	/** The form group to use */
	form: FormGroup;
	/** Denotes if the form is pending */
	saving = false;
	/** Check permission of update **/
	readOnly = false;
	/** Constructor */
	constructor(
		private formBuilder: FormBuilder,
		private errorService: ErrorService,
		public explainErrorsService: ExplainErrorsService,
		private userService: UserService,
		private sessionService: SessionService
	) {}
	/** Init */
	async ngOnInit() {
		this.readOnly = !(await this.sessionService.checkPermission(
			'user',
			'update'
		));

		// Init model
		if (!this.user) this.user = new User();

		this.refreshForm();

		// Update form when model loads or changes
		this.modelSubscription = this.user.subscribe(() => {
			this.refreshForm();
			this.updateForm();
		});
	}

	/** Destroy */
	ngOnDestroy() {
		this.modelSubscription.unsubscribe();
	}

	refreshForm() {
		// Create form
		const hiddenRequired = this.user.isNew() ? [Validators.required] : [];

		this.form = this.formBuilder.group(
			Object.assign(
				{
					name: new FormControl(this.user.props.name, [
						Validators.required,
					]),
					email: new FormControl(this.user.props.email, [
						Validators.email,
						Validators.required,
					]),
					role: new FormControl(this.user.props.role, [
						Validators.required,
					]),
				},
				this.user.isNew()
					? {
							password: new FormControl(
								this.user.props.password,
								[Validators.minLength(6)].concat(hiddenRequired)
							),
					  }
					: {}
			)
		);
	}
	/** Called on form submit */
	async onSubmit(): Promise<void> {
		// Saving flag
		this.saving = true;
		try {
			// Update model
			this.updateModel();
			// Creation or update ?
			if (this.user.isNew()) {
				// Creation
				const user: User = await this.userService.create(
					this.user.toPayload()
				);
				this.create.next(user);
			} else {
				// Update
				await this.userService.update(
					this.user.getId(),
					this.user.toPayload()
				);
				this.update.next();
			}
		} catch (error) {
			this.errorService.handle(error);
		}
		// Saving flag
		this.saving = false;
	}
	/** Update models properties from inputs values */
	private updateModel(): void {
		for (const key of Object.keys(this.form.controls)) {
			this.user.props[key] = this.form.get(key).value;
		}
	}
	/** Update inputs values from models properties */
	private updateForm(): void {
		this.form.setValue(
			Object.assign(
				{
					name: this.user.props.name,
					email: this.user.props.email,
					role: this.user.props.role,
				},
				this.user.isNew() ? { password: this.user.props.password } : {}
			)
		);
	}
	/** Called on deletion */
	onDelete(): void {
		this.deleting = true;
		this.userService
			.remove(this.user.getId())
			.then(() => {
				this.delete.next();
			})
			.catch((error) => this.errorService.handle(error))
			.then(() => (this.deleting = false));
	}
}
